const SAFE_HREF = /^(https?:|mailto:)/i;

/** Strips every attribute from the composer's rich HTML except a safe `href` on
 *  links. The server's bluemonday policy already discards them all (no `style`,
 *  no `class`), and the edge WAF reads Lexical's inline `style` as XSS and 403s
 *  the send — so none of it is worth sending. */
export function stripHtmlAttributes(html: string): string {
  if (html === '') return '';
  return stripAll(new DOMParser().parseFromString(html, 'text/html').body).innerHTML;
}

function stripAll(body: HTMLElement): HTMLElement {
  body.querySelectorAll('*').forEach((el) => {
    const href = el.tagName === 'A' ? el.getAttribute('href') : null;
    el.getAttributeNames().forEach((name) => el.removeAttribute(name));
    if (href !== null && SAFE_HREF.test(href)) el.setAttribute('href', href);
  });
  return body;
}

const FORMATTING_TAGS = 'b, strong, i, em, u, s, a, ul, ol, li';

/** Whether the HTML carries any formatting beyond paragraphs and line breaks. */
export function hasFormatting(html: string): boolean {
  if (html === '') return false;
  return new DOMParser().parseFromString(html, 'text/html').body.querySelector(FORMATTING_TAGS) !== null;
}

/** One spelling for the same formatting, so the editor's HTML and the server's sanitised copy of it compare
 *  equal: attributes stripped as above, and the editor's bare `<span>` text wrappers unwrapped. */
export function canonicalHtml(html: string): string {
  if (html === '') return '';
  const body = stripAll(new DOMParser().parseFromString(html, 'text/html').body);
  body.querySelectorAll('span').forEach((span) => span.replaceWith(...Array.from(span.childNodes)));
  body.normalize();
  return body.innerHTML;
}
